import React, { useState, useContext } from "react"
import { navigate } from "gatsby"
import { motion } from "framer-motion"
import { FiMenu, FiArrowLeft, FiChevronDown } from "react-icons/fi"
import { IoMdExit } from "react-icons/io"
import { FaRegUserCircle, FaBookReader } from "react-icons/fa"
import ButtonModalForm from "../ButtonModal/ButtonModalForm"
import { AuthContext } from "../../context/authContext"
import daliaLogo from "../../assets/images/daliaLogo.svg"
import { Link, useI18next, useTranslation } from "gatsby-plugin-react-i18next"

const FlipNavWrapper = () => {
  const context = useContext(AuthContext)
  const { user, logout } = useContext(AuthContext)

  return (
    <div className="navbar-container " style={{ backgroundColor: "#FAF8FD" }}>
      <FlipNav context={context} user={user} />
      <div className="h-72" />
    </div>
  )
}

const FlipNav = ({ context, user }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <nav className="navbar-main">
      <NavLeft context={context} user={user} />
      <NavRight setIsOpen={setIsOpen} user={user} />
      <NavMenu isOpen={isOpen} context={context} user={user} />
    </nav>
  )
}

const Logo = () => {
  return (
    <Link to="/">
      <img src={daliaLogo} alt="DaliaEmpower" />
    </Link>
  )
}

const NavLeft = ({ user }) => {
  const { t } = useTranslation("navbar")
  const menuItems = t("menuItems", { returnObjects: true })
  const context = useContext(AuthContext)

  return (
    <div className="nav-left">
      <Logo />
      {menuItems
        .filter(item => {
          if (
            (item.text === "Membresía" || item.text === "Membership") &&
            !user
          ) {
            return false
          }
          if (item.text === "Live" && context.userOnLive !== true) {
            return false
          }
          return true
        })
        .map((item, index) => (
          <NavLink key={index} text={item.text} to={item.to} />
        ))}
    </div>
  )
}

const NavLink = ({ text, to }) => {
  return (
    <Link to={to} className="navbar-links">
      <motion.div whileHover={{ y: -30 }}>
        <span className="link-inactive">{text}</span>
        <span className="link-active">{text}</span>
      </motion.div>
    </Link>
  )
}

const NavRight = ({ setIsOpen, user }) => {
  const [open, setOpen] = useState(false)
  const [openLanguage, setOpenLanguage] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const { language, languages, changeLanguage } = useI18next()
  const { t } = useTranslation("navbar")
  const context = useContext(AuthContext)

  return (
    <div className="nav-right">
      {!user && !context.userOnLive && (
        <ButtonModalForm
          buttonClass="nav-sign-in"
          title={t("loginText")}
          modalOpen={modalOpen}
          onOpen={() => setModalOpen(true)}
          onClose={() => setModalOpen(false)}
        />
      )}

      {(user || context.userOnLive) && (
        <motion.div whileTap={{ scale: 0.95 }} className="dropdown-menu">
          <motion.div
            animate={open ? "open" : "closed"}
            style={{ position: "relative" }}
          >
            <button
              onClick={() => setOpen(pv => !pv)}
              className="dropdown-button"
            >
              {t("profileText")}
              <motion.span variants={iconVariants}>
                <FiChevronDown />
              </motion.span>
            </button>

            <motion.ul
              initial={wrapperVariants.closed}
              variants={wrapperVariants}
              style={{ originY: "top", translateX: "-50%" }}
            >
              <OptionMembership
                setOpen={setOpen}
                Icon={FaBookReader}
                text={t("profileMembership")}
              />
              <Option
                setOpen={setOpen}
                Icon={FaRegUserCircle}
                text={t("profileProfile")}
              />
              <OptionLogout
                setOpen={setOpen}
                Icon={IoMdExit}
                text={t("profileLogout")}
              />
            </motion.ul>
          </motion.div>
        </motion.div>
      )}
      <div className="dropdown-menu-language">
        <motion.div
          animate={openLanguage ? "open" : "closed"}
          style={{ position: "relative" }}
        >
          <button
            onClick={() => setOpenLanguage(pv => !pv)}
            className="dropdown-button"
          >
            {language.toUpperCase()}
            <motion.span variants={iconVariants}>
              <FiChevronDown />
            </motion.span>
          </button>

          <motion.ul
            initial={wrapperVariants.closed}
            variants={wrapperVariants}
            style={{ originY: "top", translateX: "-50%" }}
          >
            {languages
              .filter(lng => lng !== language) // Filter out the currently selected language
              .map(lng => (
                <li key={lng} className="dropdown-option">
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      changeLanguage(lng)
                    }}
                  >
                    {lng}
                  </a>
                </li>
              ))}
          </motion.ul>
        </motion.div>
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="nav-button"
        onClick={() => setIsOpen(pv => !pv)}
        aria-label="Toggle Menu"
      >
        <FiMenu />
      </motion.button>
    </div>
  )
}

const NavMenu = ({ isOpen, user }) => {
  const { t } = useTranslation("navbar")
  const menuItems = t("menuItems", { returnObjects: true })
  const context = useContext(AuthContext)

  return (
    <motion.div
      variants={menuVariants}
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      className="menu-mobile"
    >
      {menuItems
        .filter(item => {
          if (
            (item.text === "Membresía" || item.text === "Membership") &&
            !user
          ) {
            return false
          }
          if (item.text === "Live" && !context.userOnLive) {
            return false
          }
          return true
        })
        .map((item, index) => (
          <MenuLink key={index} text={item.text} to={item.to} />
        ))}
    </motion.div>
  )
}

const MenuLink = ({ text, to }) => {
  return (
    <Link to={to}>
      <motion.div variants={menuLinkVariants} className="menu-link">
        <motion.div whileHover={{ y: -30 }}>
          <span className="link-inactive">{text}</span>
          <span className="link-active">{text}</span>
        </motion.div>
        <motion.span variants={menuLinkArrowVariants}>
          <FiArrowLeft className="arrow" />
        </motion.span>
      </motion.div>
    </Link>
  )
}

const Option = ({ text, Icon, setOpen }) => {
  return (
    <motion.li
      variants={itemVariants}
      onClick={() => {
        setOpen(false)
        navigate("/profile")
      }}
      className="dropdown-option"
    >
      <motion.span variants={actionIconVariants}>
        <Icon className="icon" />
      </motion.span>
      <span>{text}</span>
    </motion.li>
  )
}

const OptionMembership = ({ text, Icon, setOpen }) => {
  return (
    <motion.li
      variants={itemVariants}
      onClick={() => {
        setOpen(false)
        navigate("/membresia")
      }}
      className="dropdown-option"
    >
      <motion.span variants={actionIconVariants}>
        <Icon className="icon" />
      </motion.span>
      <span>{text}</span>
    </motion.li>
  )
}

const OptionLogout = ({ text, Icon, setOpen }) => {
  const { logout } = useContext(AuthContext)

  const OnLogout = () => {
    logout()
    setTimeout(() => {
      navigate("/")
    }, 200)
  }

  return (
    <motion.li
      variants={itemVariants}
      onClick={() => {
        setOpen(false)
        OnLogout()
      }}
      className="dropdown-option"
    >
      <motion.span variants={actionIconVariants}>
        <Icon className="icon" />
      </motion.span>
      <span>{text}</span>
    </motion.li>
  )
}

export default FlipNavWrapper

const menuVariants = {
  open: {
    scaleY: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  closed: {
    scaleY: 0,
    transition: {
      when: "afterChildren",
      staggerChildren: 0.1,
    },
  },
}

const menuLinkVariants = {
  open: {
    y: 0,
    opacity: 1,
  },
  closed: {
    y: -10,
    opacity: 0,
  },
}

const menuLinkArrowVariants = {
  open: {
    x: 0,
  },
  closed: {
    x: -4,
  },
}

const wrapperVariants = {
  open: {
    scaleY: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  closed: {
    scaleY: 0,
    transition: {
      when: "afterChildren",
      staggerChildren: 0.1,
    },
  },
}

const iconVariants = {
  open: { rotate: 180 },
  closed: { rotate: 0 },
}

const itemVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      when: "beforeChildren",
    },
  },
  closed: {
    opacity: 0,
    y: -15,
    transition: {
      when: "afterChildren",
    },
  },
}

const actionIconVariants = {
  open: { scale: 1, y: 0 },
  closed: { scale: 0, y: -7 },
}
